import React, { useCallback, useEffect, useState } from 'react';
import client, { endpoints, errText } from '../../api/client';
import { dt } from '../../lib/format';

// Nothing reaches the public wall without passing through here. Publishing is a
// deliberate act, and a review tied to a completed job carries a "verified" flag
// that the storefront can show honestly.
export default function Reviews() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('pending');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await client.get(endpoints.reviews.all, { params: { status: filter || undefined } });
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (e) { setErr(errText(e)); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const act = (fn) => async () => {
    setBusy(true); setErr('');
    try { await fn(); await load(); } catch (e) { setErr(errText(e)); }
    setBusy(false);
  };

  return (
    <section className="section">
      <div className="section-head"><h1>Reviews</h1></div>
      {err && <p className="alert alert-err">{err}</p>}

      <div className="filters">
        {['pending', 'published', 'rejected', ''].map((s) => (
          <button key={s || 'all'} className={filter === s ? 'chip chip-on' : 'chip'} onClick={() => setFilter(s)}>
            {s || 'all'} {counts[s] ? `(${counts[s]})` : ''}
          </button>
        ))}
      </div>

      {items.length === 0 && <p className="muted">Nothing here.</p>}

      {items.map((r) => (
        <div key={r._id} className="card review-card">
          <div className="section-head">
            <h2>
              {'★'.repeat(r.rating)}<span className="muted">{'★'.repeat(5 - r.rating)}</span>
              {' '}{r.title || ''}
            </h2>
            <span className="muted small">{dt(r.createdAt)}</span>
          </div>
          <p className="prewrap">{r.body}</p>
          <p className="muted small">
            {r.name}{r.company ? ` · ${r.company}` : ''}
            {r.verified
              ? <span className="badge badge-completed">verified customer · {r.bookingRef}</span>
              : <span className="badge">unverified — not matched to a booking</span>}
            {r.serviceKey ? ` · ${r.serviceKey}` : ''}{r.city ? ` · ${r.city}` : ''}
          </p>

          {r.reply && <blockquote className="reply"><strong>Your reply:</strong> {r.reply}</blockquote>}

          <div className="btn-row">
            {r.status !== 'published' && (
              <button className="btn-primary" disabled={busy}
                      onClick={act(() => client.put(endpoints.reviews.moderate(r._id), { status: 'published' }))}>Publish</button>
            )}
            {r.status !== 'rejected' && (
              <button className="btn-danger" disabled={busy}
                      onClick={act(() => client.put(endpoints.reviews.moderate(r._id), { status: 'rejected' }))}>Reject</button>
            )}
            {r.status === 'published' && (
              <button className="btn-ghost" disabled={busy}
                      onClick={act(() => client.put(endpoints.reviews.moderate(r._id), { featured: !r.featured }))}>
                {r.featured ? 'Unpin from homepage' : 'Pin to homepage'}
              </button>
            )}
          </div>

          <form className="inline-form" onSubmit={(e) => {
            e.preventDefault();
            const reply = e.target.elements.reply.value;
            if (!reply.trim()) return;
            act(() => client.put(endpoints.reviews.reply(r._id), { reply }))();
          }}>
            <input name="reply" placeholder="Reply publicly — answering a bad review beats hiding it" defaultValue={r.reply} />
            <button className="btn-ghost" disabled={busy}>Save reply</button>
          </form>
        </div>
      ))}
    </section>
  );
}
